import * as React from 'react';
import { graphql } from 'gatsby';
import Arrow from 'assets/arrow_right.inline.svg';
import IconButton from '@mui/material/IconButton';
import useIsMobile from 'core/hooks/useIsMobile';
import useHasIntersected from 'core/hooks/useHasIntersected';
import { useIntl } from 'react-intl';
import * as styles from './Carousel.module.scss';
import { CarouselImageData } from '../../lib/types';
import CarouselItem from './CarouselItem';

export interface CarouselProps {
    images: ReadonlyArray<CarouselImageData>;
    subText: React.ReactNode;
}

export default function Carousel({ images, subText }: CarouselProps) {
    const isMobile = useIsMobile();
    const imagesLength = React.useMemo(
        () => (isMobile ? images.length : images.length - 1),
        [images.length, isMobile],
    );
    const { formatMessage } = useIntl();
    const [current, setCurrent] = React.useState(0);
    const rootRef = React.useRef(null);
    const hasIntersected = useHasIntersected(rootRef);

    React.useEffect(() => {
        setCurrent(0);
    }, [imagesLength]);

    const onNext = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setCurrent((prev) => (prev + 1) % imagesLength);
        },
        [imagesLength],
    );

    const onPrevious = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();

            setCurrent((prev) => (prev + imagesLength - 1) % imagesLength);
        },
        [imagesLength],
    );

    return (
        <section className={styles.root} ref={rootRef}>
            <div className={styles.slidesWrapper}>
                <IconButton
                    className={styles.previous}
                    onClick={onPrevious}
                    aria-label={formatMessage({ id: 'carousel.previous' })}
                >
                    <Arrow />
                </IconButton>
                <IconButton
                    className={styles.next}
                    onClick={onNext}
                    aria-label={formatMessage({ id: 'carousel.next' })}
                >
                    <Arrow />
                </IconButton>
                <ul className={styles.slides}>
                    {images.map(({ image, imageAlt }, idx) => {
                        return (
                            <CarouselItem
                                key={`${image?.id}_${idx}`}
                                image={image}
                                idx={idx}
                                current={current}
                                hasIntersected={hasIntersected}
                                imageAlt={imageAlt}
                            />
                        );
                    })}
                </ul>
            </div>
            <p className={styles.subText}>{subText}</p>
        </section>
    );
}

/* eslint-disable max-len */
export const query = graphql`
    fragment CarouselImage on File {
        id
        publicURL
        alt
        mobile: childImageSharp {
            gatsbyImageData(
                width: 284
                height: 242
                sizes: "(max-width: 430px) calc(100vw - 146px), (max-width: 1514px) calc((100vw - 280px) / 2), 686px"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
                breakpoints: [186, 205, 372, 382, 410, 448, 558, 615]
            )
        }
        desktop: childImageSharp {
            gatsbyImageData(
                width: 686
                height: 584
                sizes: "(max-width: 430px) calc(100vw - 146px), (max-width: 1514px) calc((100vw - 280px) / 2), 686px"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
            )
        }
    }
    fragment LocationCarouselImage on LocationImage {
        imageAlt
        image {
            id
            file {
                ...CarouselImage
            }
        }
    }
`;
